import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, setDoc, doc, deleteDoc } from 'firebase/firestore';
import { getSubjectTeachersForClass, getSubjectTeachersForSubject, assignSubjectTeacher } from '../services/SubjectTeacherService';
import './TeacherSubjectView.css';

const SUBJECT_CATEGORIES = [
  { value: 'Mathematics', label: 'Mathematics', icon: '🔢' },
  { value: 'English', label: 'English', icon: '📖' },
  { value: 'Science', label: 'Science', icon: '🧪' },
  { value: 'Biology', label: 'Biology', icon: '🌿' },
  { value: 'Chemistry', label: 'Chemistry', icon: '⚗️' },
  { value: 'Social Studies', label: 'Social Studies', icon: '🌍' },
  { value: 'History', label: 'History', icon: '⏰' },
  { value: 'Spanish', label: 'Spanish', icon: '💬' },
  { value: 'French', label: 'French', icon: '✍️' },
  { value: 'Information Technology', label: 'Information Technology', icon: '💻' },
  { value: 'Physical Education', label: 'Physical Education', icon: '🏃' },
  { value: 'Arts', label: 'Arts', icon: '🎨' },
  { value: 'Agriculture', label: 'Agriculture', icon: '🌱' },
  { value: 'Principles of Accounts (POA)', label: 'Principles of Accounts (POA)', icon: '💳' },
  { value: 'Principles of Business (POB)', label: 'Principles of Business (POB)', icon: '💼' },
  { value: 'Building Technology', label: 'Building Technology', icon: '🔨' },
  { value: 'Home Economics', label: 'Home Economics', icon: '🏠' },
  { value: 'Woodwork', label: 'Woodwork', icon: '🪵' },
  { value: 'Reading Comprehension', label: 'Reading Comprehension', icon: '📚' },
  { value: 'Penmanship', label: 'Penmanship', icon: '✏️' }
];

function TeacherSubjectView({ teacherData, onClose }) {
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [subjectTeachers, setSubjectTeachers] = useState({});
  const [availableTeachers, setAvailableTeachers] = useState({});
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedSubjectForAssignment, setSelectedSubjectForAssignment] = useState(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const [loadingTeachers, setLoadingTeachers] = useState(false);

  const loadTeacherClasses = useCallback(async () => {
    try {
      const classesQuery = query(
        collection(db, 'classes'),
        where('teacherId', '==', teacherData.uid)
      );
      const snapshot = await getDocs(classesQuery);
      const loadedClasses = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setClasses(loadedClasses);
      if (loadedClasses.length > 0) {
        setSelectedClassId(loadedClasses[0].id);
      }
    } catch (error) {
      console.error('Failed to load classes:', error);
    }
  }, [teacherData]);

  const loadSubjects = useCallback(async () => {
    setLoading(true);
    try {
      if (teacherData.teacherType === 'subject') {
        // Load subjects from subjectTeacherSubjects collection
        const subjectsQuery = query(
          collection(db, 'subjectTeacherSubjects'),
          where('teacherId', '==', teacherData.uid)
        );
        const snapshot = await getDocs(subjectsQuery);
        const subjectsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSubjects(subjectsData);
      } else {
        // Load subjects from classes collection for regular teachers
        const allSubjects = [];
        for (const cls of classes) {
          const subjectsSnapshot = await getDocs(
            collection(db, 'classes', cls.id, 'subjects')
          );
          subjectsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            if (data.teacherId === teacherData.uid) {
              allSubjects.push({
                id: doc.id,
                ...data,
                className: cls.name
              });
            }
          });
        }
        setSubjects(allSubjects);
      }
    } catch (error) {
      console.error('Failed to load subjects:', error);
    } finally {
      setLoading(false);
    }
  }, [classes, teacherData]);

  const getSubjectCode = (category) => {
    return category.split(' ').slice(0, 2).map(word => word[0]).join('').toUpperCase();
  };

  useEffect(() => {
    loadTeacherClasses();
  }, [loadTeacherClasses]);

  useEffect(() => {
    if (classes.length > 0) {
      loadSubjects();
    }
  }, [classes, loadSubjects]);

  const handleAddSubject = async () => {
    if (!selectedCategory) {
      setMessage('Please select a subject category');
      return;
    }

    // Subject teachers can add subjects without a class
    if (teacherData.teacherType === 'subject') {
      const existingSubject = subjects.find(s => s.category === selectedCategory && s.teacherId === teacherData.uid);
      if (existingSubject) {
        setMessage('This subject is already added');
        return;
      }

      setSaving(true);
      setMessage('');

      try {
        const subjectId = crypto.randomUUID();
        const subjectData = {
          id: subjectId,
          name: selectedCategory,
          category: selectedCategory,
          classId: null, // No class assigned yet - admin will assign
          teacherId: teacherData.uid,
          isActive: true,
          createdAt: new Date().toISOString()
        };

        // Save to a separate collection for subject teacher subjects
        await setDoc(
          doc(db, 'subjectTeacherSubjects', subjectId),
          subjectData
        );

        setSelectedCategory('');
        await loadSubjects();
        setMessage('Subject added successfully. Admin will assign you to classes.');
      } catch (error) {
        console.error('Failed to add subject:', error);
        setMessage('Failed to add subject');
      } finally {
        setSaving(false);
      }
      return;
    }

    // Regular teachers need a class
    if (!selectedClassId) {
      setMessage('Please select a class');
      return;
    }

    const existingSubject = subjects.find(s => s.category === selectedCategory && s.classId === selectedClassId);
    if (existingSubject) {
      setMessage('This subject is already added for the selected class');
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      const subjectId = crypto.randomUUID();
      const subjectData = {
        id: subjectId,
        name: selectedCategory,
        category: selectedCategory,
        classId: selectedClassId,
        teacherId: teacherData.uid,
        isActive: true,
        createdAt: new Date().toISOString()
      };

      await setDoc(
        doc(db, 'classes', selectedClassId, 'subjects', subjectId),
        subjectData
      );

      setSelectedCategory('');
      await loadSubjects();
      setMessage('Subject added successfully');
    } catch (error) {
      console.error('Failed to add subject:', error);
      setMessage('Failed to add subject');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSubject = async (subject) => {
    if (!window.confirm(`Delete ${subject.name}?`)) return;

    try {
      await deleteDoc(doc(db, 'classes', subject.classId, 'subjects', subject.id));
      await loadSubjects();
      setMessage('Subject deleted');
    } catch (error) {
      console.error('Failed to delete subject:', error);
      setMessage('Failed to delete subject');
    }
  };

  const handleToggleActive = async (subject) => {
    try {
      await setDoc(
        doc(db, 'classes', subject.classId, 'subjects', subject.id),
        { ...subject, isActive: !subject.isActive },
        { merge: true }
      );
      await loadSubjects();
    } catch (error) {
      console.error('Failed to update subject:', error);
      setMessage('Failed to update subject');
    }
  };

  const loadSubjectTeachers = useCallback(async () => {
    const teachersMap = {};
    for (const cls of classes) {
      const result = await getSubjectTeachersForClass(cls.id);
      if (result.success) {
        teachersMap[cls.id] = result.subjectTeachers;
      }
    }
    setSubjectTeachers(teachersMap);
  }, [classes]);

  const loadAvailableTeachersForSubject = async (subjectName) => {
    setLoadingTeachers(true);
    const result = await getSubjectTeachersForSubject(subjectName);
    setLoadingTeachers(false);
    if (result.success) {
      setAvailableTeachers(prev => ({ ...prev, [subjectName]: result.teachers }));
    }
  };

  const handleAssignTeacher = async (subject) => {
    setSelectedSubjectForAssignment(subject);
    loadAvailableTeachersForSubject(subject.name);
    setShowAssignModal(true);
  };

  const handleConfirmAssignment = async () => {
    if (!selectedSubjectForAssignment || !selectedTeacherId) return;
    
    const result = await assignSubjectTeacher(selectedTeacherId, selectedSubjectForAssignment.classId, selectedSubjectForAssignment.name);
    if (result.success) {
      setMessage('Teacher assigned successfully');
      setShowAssignModal(false);
      setSelectedSubjectForAssignment(null);
      setSelectedTeacherId(null);
      await loadSubjectTeachers();
    } else {
      setMessage(result.error || 'Failed to assign teacher');
    }
  };

  useEffect(() => {
    if (classes.length > 0) {
      loadSubjectTeachers();
    }
  }, [classes, loadSubjectTeachers]);

  return (
    <div className="teacher-subject-view">
      <div className="subject-header">
        <button className="back-button" onClick={onClose}>
          ← Back
        </button>
        <h2>Manage My Subjects</h2>
      </div>

      {message && (
        <div className={`message-banner ${message.includes('Failed') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <div className="add-subject-section">
        {teacherData.teacherType !== 'subject' && (
          <>
            <h3>Add New Subject</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Select Class</label>
                <select
                  value={selectedClassId}
                  onChange={(e) => setSelectedClassId(e.target.value)}
                >
                  {classes.map(cls => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name} ({cls.grade})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Select Subject</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">Choose a subject</option>
                  {SUBJECT_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                className="add-subject-button"
                onClick={handleAddSubject}
                disabled={saving || !selectedCategory}
              >
                {saving ? 'Adding...' : '+ Add Subject'}
              </button>
            </div>
          </>
        )}
        {teacherData.teacherType === 'subject' && (
          <>
            <h3>Add Your Subjects</h3>
            <p className="info-text">Add subjects you teach. The admin will assign you to specific classes.</p>
            <div className="form-row">
              <div className="form-group">
                <label>Select Subject</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">Choose a subject</option>
                  {SUBJECT_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                className="add-subject-button"
                onClick={handleAddSubject}
                disabled={saving || !selectedCategory}
              >
                {saving ? 'Adding...' : '+ Add Subject'}
              </button>
            </div>
          </>
        )}
      </div>

      <div className="subjects-section">
        <h3>My Subjects</h3>
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading subjects...</p>
          </div>
        ) : subjects.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📚</div>
            <h3>No subjects added yet</h3>
            <p>Add subjects above to manage them here</p>
          </div>
        ) : (
          <div className="subjects-list">
            {subjects.map(subject => {
              const category = SUBJECT_CATEGORIES.find(c => c.value === subject.category) || { icon: '📚', label: subject.category };
              return (
                <div key={subject.id} className="subject-card">
                  <div className="subject-icon">{category.icon}</div>
                  <div className="subject-info">
                    <h4>{category.label}</h4>
                    <p className="subject-class">Class: {subject.className || 'Unknown'}</p>
                    <p className="subject-code">Code: {getSubjectCode(subject.category)}</p>
                    {subjectTeachers[subject.classId]?.find(st => st.subject === subject.name) && (
                      <p className="assigned-teacher">
                        Teacher: {subjectTeachers[subject.classId].find(st => st.subject === subject.name).teacherId.substring(0, 8)}
                      </p>
                    )}
                    <span className={`status-badge ${subject.isActive ? 'active' : 'inactive'}`}>
                      {subject.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="subject-actions">
                    <button
                      className="assign-teacher-button"
                      onClick={() => handleAssignTeacher(subject)}
                    >
                      👤+
                    </button>
                    <button
                      className="toggle-button"
                      onClick={() => handleToggleActive(subject)}
                    >
                      {subject.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      className="delete-button"
                      onClick={() => handleDeleteSubject(subject)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showAssignModal && selectedSubjectForAssignment && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Assign {selectedSubjectForAssignment.name} Teacher</h3>
              <button className="close-button" onClick={() => setShowAssignModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {loadingTeachers ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading teachers...</p>
                </div>
              ) : (!availableTeachers[selectedSubjectForAssignment.name] || availableTeachers[selectedSubjectForAssignment.name].length === 0) ? (
                <div className="empty-state">
                  <p>No {selectedSubjectForAssignment.name} teachers available</p>
                  <p className="empty-hint">Contact admin to add a {selectedSubjectForAssignment.name} teacher</p>
                </div>
              ) : (
                <div className="teachers-list">
                  {availableTeachers[selectedSubjectForAssignment.name].map(teacher => (
                    <div 
                      key={teacher.id} 
                      className={`teacher-option ${selectedTeacherId === teacher.id ? 'selected' : ''}`}
                      onClick={() => setSelectedTeacherId(teacher.id)}
                    >
                      <div className="teacher-details">
                        <h4>{teacher.name}</h4>
                        <p>{teacher.specialtySubject} Teacher</p>
                      </div>
                      {selectedTeacherId === teacher.id && (
                        <span className="check-icon">✓</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="cancel-button" onClick={() => setShowAssignModal(false)}>Cancel</button>
              <button 
                className="confirm-button" 
                disabled={!selectedTeacherId}
                onClick={handleConfirmAssignment}
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeacherSubjectView;
